<template>
    <div
        class="content-wrapper position-relative"
    >
        <DebugContent
            title="DEBUG: Dynamic Page from CMS"
            class="ma-8"
        >
            <template #expand>
                <div
                    v-if="contentDisplayObj && !lo_isError(contentDisplayObj)"
                >
                    <h3>Meta</h3>
                    <ul
                        v-if="contentDisplayObj.meta"
                    >
                        <li
                            v-for="(v, k) in contentDisplayObj.meta"
                            :key="k"
                        >
                            <strong>{{ k }}:</strong>
                            {{ v }}
                        </li>
                    </ul>
                </div>
            </template>
        </DebugContent>

        <!-- Content / Error / Progress -->
        <template
            v-if="!currentError && contentDisplayObj && !lo_isError(contentDisplayObj) && contentDisplayObj.content && contentDisplayObj.meta && contentDisplayObj?.baseComponent"
        >
            <Suspense>
                <component
                    :is="contentComponents[contentDisplayObj.baseComponent]"
                    :page-key="pageKey"
                    :content-data="contentDisplayObj.content"
                    :content-meta="contentDisplayObj.meta"
                ></component>
            </Suspense>
        </template>
        <template
            v-else-if="currentError"
        >
            <ContentPageError :error="currentError"></ContentPageError>
        </template>
        <template
            v-else
        >
            <!-- Progress Indicator -->
            <div

                class="
                    d-flex
                    flex-column
                    align-center
                    justify-center
                    content-wrapper-progress
                    absolute-center
                    h-100
                "
            >
                <v-progress-circular
                    color="accent"
                    width="8"
                    size="62"
                    indeterminate
                ></v-progress-circular>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import ContentPageError from '~/components/error/ContentPageError/ContentPageError.vue';

    import type { ContentComponentType, GetContentDataResponse } from '~/types';
    import type { NuxtError } from '#app';
    import { useContent } from '~/composables';
    import { isError as lo_isError } from 'es-toolkit';
    import type { WatchStopHandle } from 'vue';
    import { useSeoMeta } from '#imports';
    import { pathJoin, getPseudoHash } from 'x1-base/utils';
    import { whenever } from '@vueuse/core';

    definePageMeta({
        scrollToTop: true,
        path: '/content/:catchAll(.*)',
        alias: [
            '/:catchAll(.*)'
        ]
    });

    const contentComponents: Record<ContentComponentType, Nullable<ReturnType<typeof resolveComponent>>> = {
        TypePage: resolveComponent('ContentTypePage'),
        TypeCollection: resolveComponent('ContentTypeCollection'),
        TypeArticle: resolveComponent('ContentTypeArticle'),
        TypeProduct: resolveComponent('ContentTypeProduct'),
        None: null
    };

    const $config = useRuntimeConfig();
    const route = useRoute();
    const { getPageContent } = useContent();
    const brandTitle = $config.public.brand.title;
    const docTitle = ref();
    const docMeta = ref();

    useHead({
        title: docTitle,
        meta: docMeta
    });

    const pageKey = computed<string>(() => `keyContent_${getPseudoHash(route.fullPath)}`);

    async function getContent(): Promise<GetContentDataResponse | NuxtError> {
        const result = await getPageContent(route);

        if (lo_isError(result)) {
            throw result;
        }

        return result;
    }

    const {
        data: contentDisplayObj,
        error: currentError
    } = useLazyAsyncData<GetContentDataResponse | NuxtError>(
        pageKey.value,
        () => getContent()
    );

    const unWatchContentDisplayObj = whenever(
        contentDisplayObj,
        handleContent,
        {
            immediate: true,
            once: true
        }
    );
    const unWatchError = whenever(
        currentError,
        handleError,
        {
            immediate: true,
            once: true
        }
    );

    async function handleContent(contentObj: GetContentDataResponse | NuxtError): Promise<void> {
        if (!lo_isError(contentObj) && contentObj.meta.type === 'redirect' && contentObj?.meta.path) {
            const redirectPath = pathJoin('/', contentObj.meta.path);
            await navigateTo(redirectPath, { redirectCode: 301 });
        } else {
            setPageSeo();
        }
    }

    function handleError(errorObj: Error | NuxtError): void {
        if (isApiDown(errorObj)) {
            // Fatal Error
            showError({ statusCode: 503, message: 'Site Offline', fatal: true });
        } else {
            const errMsg = 'Page Not Found';
            docTitle.value = `${errMsg} | ${brandTitle}`;
            setResponseStatus(useRequestEvent()!, 404, errMsg);
        }
    }

    // NOTE: Maybe we don't need to do this unless we are on the server?'
    function setPageSeo() {
        if (!currentError.value && contentDisplayObj.value && 'meta' in contentDisplayObj.value && 'content' in contentDisplayObj.value) {
            const { meta } = contentDisplayObj.value;

            if (meta.type === 'redirect') {
                return;
            }

            const metaItems = [];
            const baseURL = $config.public.environment.homeUrl;


            const title = `${meta.seoTitle || meta.title} | ${brandTitle}`;
            docTitle.value = title;

            const serverMetaObj: Record<string, string> = {
                ogTitle: title,
                twitterTitle: title,
                ogUrl: `${baseURL}${route.fullPath}`
            };

            if (meta.seoDescription) {
                metaItems.push({
                    name: 'description',
                    content: meta.seoDescription
                });

                serverMetaObj.ogDescription = meta.seoDescription;
                serverMetaObj.twitterDescription = meta.seoDescription;
            }

            if (meta.seoKeywords) {
                metaItems.push({
                    name: 'keywords',
                    content: meta.seoKeywords
                });
            }

            docMeta.value = metaItems;

            if (import.meta.server) {
                useSeoMeta(serverMetaObj);
            }
        }
    }

    function isApiDown(err: NuxtError | Error | void): boolean {
        return Boolean(err?.toString()?.includes('no response'));
    };

    onBeforeUnmount(() => {
        unWatchError?.();
        unWatchContentDisplayObj?.();
    });
</script>

<style lang="scss">
.content-wrapper {
    min-height: 60vh;
}
</style>
